import { getCountyLabel } from '../../config/vendorProfileOptions';
import { getVendorServiceLabel } from '../../util/vendorServiceLabels';

const MAX_LENGTH = 160;
const HOME_COUNTY = 'nevada';
// (services, counties) to show, richest first; the first one that fits under MAX_LENGTH wins.
const FIT_STEPS = [[3, 3], [3, 2], [3, 1], [2, 1], [1, 1]];

const asArray = value => (Array.isArray(value) ? value.filter(Boolean) : []);
const unique = items => [...new Set(items)];

const truncateAtWord = (text, maxLength) => {
  if (text.length <= maxLength) {
    return text;
  }
  const cut = text.slice(0, maxLength - 1);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
  return `${base.replace(/[\s,;:—–-]+$/, '')}…`;
};

// Same field reads as the /vendors directory (server/api/matched-vendors.js).
const getServiceLabels = publicData =>
  unique(
    asArray(publicData.services_offered_vendor || publicData.servicesOffered).map(
      getVendorServiceLabel
    )
  );

const getCountyNames = publicData => {
  const counties = asArray(publicData.counties_served || publicData.countiesServed);
  const ordered = counties.includes(HOME_COUNTY)
    ? [HOME_COUNTY, ...counties.filter(c => c !== HOME_COUNTY)]
    : counties;
  return unique(ordered.map(c => getCountyLabel(c).replace(/ County$/, '')));
};

/**
 * Meta description for a vendor's public profile page, built from the same publicData the
 * /vendors directory card shows. Kept under 160 characters by showing fewer services and
 * counties; clauses with no data are dropped. With neither services nor counties it uses a
 * name-only sentence. The vendor's own bio is deliberately not used: it would put the vendor's
 * self-made claims (e.g. "licensed and insured") in CAFD's search snippet (TOS §2.2 No-Vetting).
 *
 * @param {Object} profileUser - user entity with attributes.profile
 * @param {Object} intl - react-intl object
 * @param {string} marketplaceName
 * @returns {string|null} description, or null to let <Page> use its default
 */
export const buildVendorMetaDescription = (profileUser, intl, marketplaceName) => {
  const profile = profileUser?.attributes?.profile;
  if (!profile) {
    return null;
  }
  const publicData = profile.publicData || {};
  const name = publicData.business_name || publicData.businessName || profile.displayName;
  if (!name) {
    return null;
  }

  const services = getServiceLabels(publicData);
  const counties = getCountyNames(publicData);

  if (services.length === 0 && counties.length === 0) {
    return truncateAtWord(
      intl.formatMessage({ id: 'ProfilePage.metaDescriptionNameOnly' }, { name, marketplaceName }),
      MAX_LENGTH
    );
  }

  const describe = (serviceCount, countyCount) => {
    const shownServices = services.slice(0, serviceCount);
    const shownCounties = counties.slice(0, countyCount);
    const vars = {
      name,
      marketplaceName,
      services: intl.formatList(shownServices, { type: 'conjunction' }),
      areas: intl.formatMessage(
        { id: 'ProfilePage.metaCounties' },
        {
          count: shownCounties.length,
          list: intl.formatList(shownCounties, { type: 'conjunction' }),
        }
      ),
    };
    const id =
      shownServices.length && shownCounties.length
        ? 'ProfilePage.metaDescription'
        : shownServices.length
        ? 'ProfilePage.metaDescriptionServices'
        : 'ProfilePage.metaDescriptionAreas';
    return intl.formatMessage({ id }, vars);
  };

  const candidates = FIT_STEPS.map(([s, c]) => describe(s, c));
  const fitting = candidates.find(text => text.length <= MAX_LENGTH);
  return fitting || truncateAtWord(candidates[candidates.length - 1], MAX_LENGTH);
};
